import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useController, UseFormReturn } from 'react-hook-form'
import { Row, Table } from '@tanstack/react-table'

import { InputFormValues } from '@/modules/CombinedLogs/types'
import { Input } from '@/ui/Input'
import { useCombinedLogsTableFormStateStore } from '@/modules/CombinedLogs/store'
import { useGetCodes } from '@/modules/CombinedLogs/api/useGetCodes'
import { Popup, PopupRef } from '@/ui/Popup'
import { EXTRA_INFO_CODES, Log, MileageLossCode } from '@/types/logs'
import { SuggestionInputTable } from '@/ui/SuggestionInputTable'

import styles from './TableFormDescriptionInput.module.scss'

import LinkIcon from '@/modules/CombinedLogs/assets/link.svg'
import CrossIcon from '@/modules/CombinedLogs/assets/cross.svg'

interface Props {
    form: UseFormReturn<InputFormValues>
    table: Table<Log>
    row?: Row<Log>
    isBulkCurtailment?: boolean
}

const MAX_LENGTH = 200
const EXTRA_INFO_MAX_LENGTH = 2000

const TableFormDescriptionInput = ({ form }: Props) => {
    const { field: reasonField } = useController({ control: form.control, name: 'selectedCodeTypeReasonId' })
    const { field: descriptionField } = useController({ control: form.control, name: 'description' })
    const { data: codes = [] } = useGetCodes()
    const { code: storeCode, reason, setReason } = useCombinedLogsTableFormStateStore()
    const code = form.watch('code')

    const reasons = codes.find((item) => item.code === code)?.reasons || []

    const handleReasonChange = (reasonValue: string) => {
        const reason = reasons.find((reason) => reason.reasonId === reasonValue)

        if (reason) {
            reasonField.onChange(reasonValue)
            setReason(reason)
            form.setValue('description', reason.reasonDescription, { shouldDirty: true })
        } else {
            form.setValue('description', reasonValue, { shouldDirty: true })
            form.setValue('selectedCodeTypeReasonId', undefined)
            setReason(null)
        }
    }

    useEffect(() => {
        if (!code || !storeCode || code === storeCode) {
            return
        }

        reason && form.setValue('description', '')
        setReason(null)
    }, [code, storeCode, descriptionField.value])

    useEffect(() => {
        setReason(null)
    }, [form.formState.submitCount])

    useEffect(() => {
        // Init reason for edit state
        if (reasonField.value && !reason) {
            const value = reasons.find((reason) => reason.reasonId === reasonField.value)
            value && setReason(value)
        }
    }, [reasonField.value, reason, reasons])

    if (!reasons.length) {
        return (
            <Input
                value={descriptionField.value}
                variant="table"
                type="text"
                transparent
                fullWidth
                placeholder={'Description'}
                maxLength={MAX_LENGTH}
                onChange={descriptionField.onChange}
            />
        )
    }

    return (
        <SuggestionInputTable
            options={reasons.map((reason) => ({
                id: reason.reasonId,
                value: reason.reasonId,
                label: reason.reasonDescription,
            }))}
            variant="table"
            type="text"
            transparent
            fullWidth
            className={styles.reasonsPopup}
            placeholder="Description"
            value={descriptionField.value}
            maxLength={MAX_LENGTH}
            onChange={handleReasonChange}
            onlyFromDropdown
        />
    )
}

const REFERENCE_PATTERN_REGEXP = /\b(?:\d{2,}|(?=[A-Z0-9/]*[A-Z])[A-Z0-9/]{2,})\b/g

const TableFormExtraInfoInput = ({ form, table, isBulkCurtailment }: Props) => {
    const popupRef = useRef<PopupRef>(null)
    const extraInfo = form.watch('extraInfo')
    const [linkTo, setLinkTo] = useState<Log | null>(null)
    const [pattern, setPattern] = useState('')
    const code = form.watch('code')
    const eventLogId = form.watch('eventLogId')

    const filteredLogsByReference = useMemo(() => {
        if (!form || !extraInfo || !pattern || (code && !(code in MileageLossCode))) {
            return []
        }

        return table
            .getRowModel()
            .rows.filter(
                (item) =>
                    !(item.original.code in MileageLossCode) &&
                    item.original.reference &&
                    item.original.reference.includes(pattern)
            )
    }, [extraInfo, form, pattern])

    const handleLinkToLog = (log: Log, event: SyntheticEvent) => {
        event.stopPropagation()
        setLinkTo(log)
        popupRef.current?.closePopup()
        form.setValue('eventLogId', log.id)
        form.setValue('extraInfo', extraInfo?.replace(pattern, log.reference))
        setPattern('')
    }

    const handleRemoveLinkTo = (event: SyntheticEvent) => {
        event.stopPropagation()
        event.preventDefault()
        setLinkTo(null)
        form.setValue('eventLogId', undefined, { shouldDirty: true })
    }

    useEffect(() => {
        if (filteredLogsByReference.length && !linkTo) {
            popupRef.current?.openPopup()
        } else {
            popupRef.current?.closePopup()
        }
    }, [filteredLogsByReference])

    useEffect(() => {
        if (!extraInfo) {
            return
        }

        const pattern = extraInfo.match(REFERENCE_PATTERN_REGEXP)

        if (!pattern) {
            setPattern('')
            return
        }

        // We expect to have only one pattern in info
        setPattern(pattern[0])
    }, [extraInfo])

    useEffect(() => {
        if (!eventLogId || (eventLogId && form.formState.submitCount)) {
            setPattern('')
            setLinkTo(null)
        }
    }, [eventLogId, form.formState.submitCount])

    useEffect(() => {
        if (!linkTo && eventLogId) {
            const linkedLog = table.getCoreRowModel().rows.find((item) => item.original.id === eventLogId)

            linkedLog && setLinkTo(linkedLog.original)
        }
    }, [eventLogId])

    return (
        <>
            <Popup
                ref={popupRef}
                placement="bottom"
                triggerComponent={
                    <Input
                        {...form.register('extraInfo')}
                        variant="table"
                        type="text"
                        transparent
                        fullWidth
                        placeholder={'Extra info'}
                        maxLength={EXTRA_INFO_MAX_LENGTH}
                    />
                }
                trigger="manual"
                className={styles.linkPopup}
                attachToTrigger
            >
                <div className={styles.titleWrapper}>
                    <p className={styles.title}>Link to disruption or other event</p>
                </div>
                {filteredLogsByReference.map((item) => (
                    <button
                        type="button"
                        key={item.id}
                        className={styles.linkItem}
                        onClick={(event) => handleLinkToLog(item.original, event)}
                    >
                        {item.original.reference}{' '}
                        {item.original.descriptionInfo.description && ` ${item.original.descriptionInfo.description}`}
                    </button>
                ))}
            </Popup>
            {linkTo && (
                <div className={styles.linkElementWrapper}>
                    <div className={styles.linkElement}>
                        <LinkIcon />
                        <span>
                            Linked to <label> {linkTo.reference}</label>
                        </span>
                    </div>
                    {!isBulkCurtailment && (
                        <button type="button" className={styles.removeLinkButton} onClick={handleRemoveLinkTo}>
                            <CrossIcon />
                        </button>
                    )}
                </div>
            )}
        </>
    )
}

const TableFormStrategyInput = ({ form }: Props) => {
    const selectedCode = form.watch('code')

    if (!EXTRA_INFO_CODES.includes(selectedCode ?? '')) {
        return null
    }

    return (
        <Input
            {...form.register('strategy')}
            variant="table"
            type="text"
            transparent
            fullWidth
            placeholder={'Strategy'}
            maxLength={MAX_LENGTH}
        />
    )
}

export const TableFormDescriptionInputs = (props: Props) => (
    <div className={styles.root}>
        <TableFormDescriptionInput {...props} />
        <TableFormExtraInfoInput {...props} />
        <TableFormStrategyInput {...props} />
    </div>
)
